import dayjs from 'dayjs'
import { isNotNullish } from 'effect/Predicate'

import { CORE_CONSTANT } from '../../constant.ts'
import { generateFieldName } from '../../functions/view/compileViewConfig/sheet/generateFieldName.ts'
import type {
  ArrayNode,
  ExpressionNode,
  FunctionNode,
  LiteralNode,
  Operator,
  VariableNode,
} from '../syntax/expression_ast.ts'
import { getOrderedComparison } from './getOrderedComparison.ts'
import type { CoreDslFormLeafPartial, CoreDslFormNodeStatePartial, CoreDslFormSnapshotVariable } from './schema.ts'

//
// CORE言語のUIフォーム用ASTを、実際のASTに変換する
//
export const convertFormToAst = (formNode: CoreDslFormNodeStatePartial): ExpressionNode | undefined => {
  const nodes = [
    ...formNode.leafs.map((leaf) => convertLeaf(leaf)),
    // eslint-disable-next-line sonarjs/todo-tag
    ...formNode.children.map((child) => convertFormToAst(child)), // TODO: and, orが混在するときの括弧が適切にできていないかも？
  ].filter(isNotNullish)
  return recursiveLogicalOperator(formNode.logicalOperator === 'and' ? '&&' : '||', nodes)
}

const recursiveLogicalOperator = (operator: '&&' | '||', nodes: ExpressionNode[]): ExpressionNode | undefined => {
  const [left] = nodes
  if (left === undefined) {
    return undefined
  }
  const right = recursiveLogicalOperator(operator, nodes.slice(1))
  if (right === undefined) {
    return left
  }

  return {
    type: 'operator',
    operator,
    left,
    right,
  }
}

// eslint-disable-next-line complexity,sonarjs/cognitive-complexity
const convertLeaf = (leaf: CoreDslFormLeafPartial): ExpressionNode | undefined => {
  const left = convertTerm(leaf.left)
  if (left === undefined) {
    return undefined
  }
  //
  // rightが不要なoperatorの処理
  //
  // 「入力なし(null)/入力あり(not_null)」を = null / != null に変換すると、評価時に
  // compileOperator の callOperatorForAny が (v ?? false) === (null ?? false) を行い、
  // boolean の false が null に潰れて「入力なし」に一致してしまう。
  // SQL filter は false と null を区別できているため、conditionalEffects 側も
  // blank/present 関数に寄せて false と null を区別する。
  if (leaf.operator === 'null' || leaf.operator === 'blank') {
    return {
      type: 'func',
      name: 'blank',
      args: [left],
    }
  }
  if (leaf.operator === 'not_null' || leaf.operator === 'present') {
    return {
      type: 'func',
      name: 'present',
      args: [left],
    }
  }

  const right = convertRightTerm(leaf.operator, leaf.right)
  if (right === undefined) {
    return undefined
  }

  //
  // 特殊なoperatorの処理（AST側では関数になるもの）
  //
  if (leaf.operator === 'in' || leaf.operator === 'not_in') {
    const inExp: ExpressionNode = {
      type: 'func',
      name: 'in',
      args: [left, right],
    }
    return leaf.operator === 'in'
      ? inExp
      : {
          type: 'func',
          name: 'not',
          args: [inExp],
        }
  }
  if (leaf.operator === 'include' || leaf.operator === 'not_include') {
    const includeExp: ExpressionNode = {
      type: 'func',
      name: 'include',
      args: [left, right, generateLiteralNode(leaf.extra?.shouldConsiderCase === true)],
    }
    return leaf.operator === 'include'
      ? includeExp
      : {
          type: 'func',
          name: 'not',
          args: [includeExp],
        }
  }
  if (leaf.operator === 'starts_with' || leaf.operator === 'not_starts_with') {
    const startsWithExp: ExpressionNode = {
      type: 'func',
      name: 'startsWith',
      args: [left, right, generateLiteralNode(leaf.extra?.shouldConsiderCase === true)],
    }
    return leaf.operator === 'starts_with'
      ? startsWithExp
      : {
          type: 'func',
          name: 'not',
          args: [startsWithExp],
        }
  }
  if (leaf.operator === 'overlap' || leaf.operator === 'not_overlap') {
    const overlapExp: ExpressionNode = {
      type: 'func',
      name: 'overlap',
      args: [left, right],
    }
    return leaf.operator === 'overlap'
      ? overlapExp
      : {
          type: 'func',
          name: 'not',
          args: [overlapExp],
        }
  }

  const orderedComparison = getOrderedComparison(leaf)
  if (orderedComparison !== undefined) {
    const order = generateArrayNode(orderedComparison.options.map((option) => option.value))
    // 集合に入っているかを `in` で確かめないのは、`in` が配列の値を「全要素が含まれるか」で判定し、
    // 空配列でも成立させてしまうため。比較そのものと同じ `rank` で判定して基準を 1 つにそろえる
    const isInOrder = (term: ExpressionNode): ExpressionNode => ({
      type: 'operator',
      operator: '>=',
      left: { type: 'func', name: 'rank', args: [term, order] },
      right: { type: 'literal', literalType: 'number', value: 0 },
    })
    return {
      type: 'func',
      name: 'and',
      args: [
        isInOrder(left),
        isInOrder(right),
        {
          type: 'operator',
          operator: orderedComparison.operator,
          left: { type: 'func', name: 'rank', args: [left, order] },
          right: { type: 'func', name: 'rank', args: [right, order] },
        },
      ],
    }
  }

  //
  // 特殊な処理
  // 文字列型の比較の場合、大文字小文字を無視するかどうかを考慮するので、equal関数を使う
  //
  const leftProperty = leaf.left?.type === 'snapshotVariable' ? leaf.left.property : undefined
  if (leftProperty?.type === 'string' && (leaf.operator === '=' || leaf.operator === '!=')) {
    const equalExp: ExpressionNode = {
      type: 'func',
      name: 'equal',
      args: [left, right, generateLiteralNode(leaf.extra?.shouldConsiderCase === true)],
    }
    return leaf.operator === '=' ? equalExp : { type: 'func', name: 'not', args: [equalExp] }
  }

  //
  // 特殊な処理
  // boolean型の列（「現在の値」= snapshotVariable）を「値を直接入力」のリテラルと = / != で
  // 比較するとき、通常の operator に落とすと評価時に callOperatorForAny が
  // (v ?? false) === (right ?? false) を行い、null セルが false に潰れて「= False」条件に
  // 一致してしまう。SQL filter は null と false を区別する（x = false / x != false はどちらも
  // null セルに一致しない三値論理）ため、null を潰さない equal 関数で組み立てて挙動を揃える。
  if (leftProperty?.type === 'boolean' && (leaf.operator === '=' || leaf.operator === '!=')) {
    const equalExp: ExpressionNode = {
      type: 'func',
      name: 'equal',
      args: [left, right],
    }
    // != は null セルも非一致にする（SQL の x != false が null セルに一致しない挙動に揃える）ため、
    // present で値の存在を確認した上で equal の否定を取る。
    return leaf.operator === '='
      ? equalExp
      : {
          type: 'func',
          name: 'and',
          args: [
            { type: 'func', name: 'present', args: [left] },
            { type: 'func', name: 'not', args: [equalExp] },
          ],
        }
  }

  //
  // 通常のoperatorの処理
  //
  const operator = convertOperator(leaf.operator)
  if (operator === undefined) {
    return undefined
  }

  //
  // 特殊な処理
  // 右辺のリテラルが日付型でdateTimeTypeがmonth,yearのときは、その関数を左辺と右辺に適用する必要がある
  //
  const rightLiteral = leaf.right?.type === 'literal' ? leaf.right : undefined
  if (
    rightLiteral?.literalType === 'date' &&
    (rightLiteral.dateTimeType === 'week' ||
      rightLiteral.dateTimeType === 'month' ||
      rightLiteral.dateTimeType === 'year')
  ) {
    return {
      type: 'operator',
      operator,
      left: {
        type: 'func',
        name: rightLiteral.dateTimeType,
        args: [left],
      },
      right: {
        type: 'func',
        name: rightLiteral.dateTimeType,
        args: [right],
      },
    }
  }
  // 同上。日付型であり、dateTimeTypeがrelativeのときは、relativeに対応する関数を適用する必要がある
  // （例）「左辺が先月に等しい」という条件のとき、そのままだと x = dateAdd(today(), -1, 'month') のような値になり、
  //      すなわち x = '2023-08-01' のような値になるが、これだと2023-08-02が一致しなくなってしまう。
  //      これを month(x) = month('2023-08-01')にすることで、月で一致させる
  if (rightLiteral?.literalType === 'date' && rightLiteral.dateTimeType === 'relative') {
    const maybeDateTimeType = rightLiteral.value.split(' ')[1]
    if (
      maybeDateTimeType === 'week' ||
      maybeDateTimeType === 'month' ||
      maybeDateTimeType === 'year' ||
      maybeDateTimeType === 'fiscal_year'
    ) {
      return {
        type: 'operator',
        operator,
        left: {
          type: 'func',
          name: maybeDateTimeType,
          args: [left],
        },
        right: {
          type: 'func',
          name: maybeDateTimeType,
          args: [right],
        },
      }
    }
  }

  return {
    type: 'operator',
    operator,
    left,
    right,
  }
}

const generateArrayNode = (values: string[]): ArrayNode => ({
  type: 'array',
  elements: values.map((value) => ({ type: 'literal', literalType: 'string', value })),
})

const generateLiteralNode = (value: boolean): LiteralNode => ({
  type: 'literal',
  literalType: 'boolean',
  value,
})

const convertOperator = (
  operator: Exclude<
    CoreDslFormLeafPartial['operator'],
    | 'in'
    | 'not_in'
    | 'not_null'
    | 'null'
    | 'blank'
    | 'present'
    | 'include'
    | 'not_include'
    | 'starts_with'
    | 'not_starts_with'
    | 'overlap'
    | 'not_overlap'
  >,
): Operator | undefined => {
  if (operator === undefined) {
    return undefined
  }
  return operator
}

const convertTerm = (
  term: CoreDslFormLeafPartial['left'],
): LiteralNode | ArrayNode | VariableNode | FunctionNode | undefined => {
  if (term === undefined) {
    return undefined
  }
  const { type } = term
  switch (type) {
    case 'literal': {
      const { literalType } = term
      switch (literalType) {
        case 'string': {
          return {
            type: 'literal',
            literalType: 'string',
            value: term.value,
          }
        }
        case 'number': {
          return {
            type: 'literal',
            literalType: 'number',
            value: term.value,
          }
        }
        case 'boolean': {
          return {
            type: 'literal',
            literalType: 'boolean',
            value: term.value,
          }
        }
        case 'date': {
          if (term.dateTimeType === 'relative') {
            const [maybeRelativeDay, maybeDateType] = term.value.split(' ')
            return {
              type: 'func',
              name: 'dateAdd',
              args: [
                {
                  type: 'func',
                  name: 'today',
                  args: [],
                },
                {
                  type: 'literal',
                  literalType: 'number',
                  value: Number.parseInt(maybeRelativeDay ?? '', 10),
                },
                {
                  type: 'literal',
                  literalType: 'string',
                  value: maybeDateType ?? '',
                },
              ],
            }
          }
          return {
            type: 'literal',
            literalType: 'date',
            value: dayjs(term.value),
          }
        }
        case 'array': {
          return {
            type: 'array',
            elements: term.value.map((value) => ({
              type: 'literal',
              literalType: 'string',
              value,
            })),
          }
        }
        // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
        default: {
          // eslint-disable-next-line sonarjs/no-dead-store,sonarjs/no-unused-vars
          const x: never = literalType
          return undefined
        }
      }
    }
    case 'snapshotVariable': {
      const day = getDaysFromSnapshotConfigBefore(term)
      const variable: VariableNode = {
        type: 'variable',
        path:
          day === 0
            ? [generateFieldName(term.nodePropertyName)]
            : [
                CORE_CONSTANT.SNAPSHOT_VARIABLE_NAME,
                `${CORE_CONSTANT.SNAPSHOT_VARIABLE_DAY_PREFIX}${day}`,
                term.property.name,
              ],
      }
      if (term.property.type === 'date' || term.property.type === 'datetime') {
        return {
          type: 'func',
          name: 'date',
          args: [variable],
        }
      }
      return variable
    }
    case 'recordNodeVariable': {
      const variable: VariableNode = {
        type: 'variable',
        path: [generateFieldName(term.nodePropertyName)],
      }
      if (term.property.type === 'date' || term.property.type === 'datetime') {
        return {
          type: 'func',
          name: 'date',
          args: [variable],
        }
      }
      return variable
    }
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    default: {
      // eslint-disable-next-line sonarjs/no-dead-store,sonarjs/no-unused-vars
      const x: never = type
      return undefined
    }
  }
}

const convertRightTerm = (
  operator: CoreDslFormLeafPartial['operator'],
  term: CoreDslFormLeafPartial['right'],
): LiteralNode | ArrayNode | VariableNode | FunctionNode | undefined => {
  if (operator === undefined) {
    return undefined
  }
  if (operator === 'not_null') {
    return {
      type: 'literal',
      literalType: 'null',
      value: null,
    }
  }
  return convertTerm(term)
}

const getDaysFromSnapshotConfigBefore = (x: CoreDslFormSnapshotVariable) => {
  const { dateSpan } = x
  switch (dateSpan) {
    case 'day': {
      return x.before
    }
    case 'week': {
      return x.before * 7
    }
    case 'month': {
      // 1月の数は不定なので「三ヶ月前」が何日分なのかは現在の日時をベースに求める必要がある
      return dayjs().diff(dayjs().subtract(x.before, 'month'), 'day')
    }
    case 'year': {
      return dayjs().diff(dayjs().subtract(x.before, 'year'), 'day')
    }
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    default: {
      // eslint-disable-next-line sonarjs/no-dead-store,sonarjs/no-unused-vars
      const x: never = dateSpan
      return 0
    }
  }
}
